import { ref, computed } from 'vue'

interface ModalInstance {
  id: string
  isOpen: boolean
  close: () => void
  isClosableOnEsc: () => boolean
}

const modalStack = ref<ModalInstance[]>([])

/** Hide page scroll while any modal is open */
function lockBodyScroll() {
  if (typeof document === 'undefined') return
  document.body.style.overflow = 'hidden'
}

/** Restore page scroll when no modals remain open */
function unlockBodyScroll() {
  if (typeof document === 'undefined') return
  document.body.style.overflow = ''
}

/** Global Esc handler - closes the top modal if it allows Esc-close */
function onGlobalKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  if (event.defaultPrevented) return
  if (modalStack.value.length === 0) return
  const top = modalStack.value[modalStack.value.length - 1]
  if (!top.isClosableOnEsc()) return
  event.preventDefault()
  top.close()
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', onGlobalKeydown)
}

export function useModalStack() {
  const pushModal = (modal: ModalInstance) => {
    if (modalStack.value.length === 0) lockBodyScroll()
    modalStack.value.push(modal)
  }

  const removeModal = (id: string) => {
    const index = modalStack.value.findIndex(m => m.id === id)
    if (index === -1) return
    modalStack.value.splice(index, 1)
    if (modalStack.value.length === 0) unlockBodyScroll()
  }

  const getTopModal = () => {
    return modalStack.value[modalStack.value.length - 1] || null
  }

  const isTopModal = (id: string) => {
    const top = getTopModal()
    return top ? top.id === id : false
  }

  const closeTopModal = () => {
    const top = getTopModal()
    if (top) {
      top.close()
    }
  }

  const stackDepth = computed(() => modalStack.value.length)

  return {
    modalStack: computed(() => modalStack.value),
    pushModal,
    removeModal,
    getTopModal,
    isTopModal,
    closeTopModal,
    stackDepth,
  }
}

